import type {
  AccountAdminDto,
  ArchivedBranchListItemDto,
  ArchivedDepartmentDto,
  BranchDto,
  CompanyDto,
  CountryDto,
  CurrencyDto,
  DepartmentDto,
  GovernorateDto,
} from "../data";
import type { ArchivedDepartment, Department } from "../domain";

const createdAt = (index: number): string =>
  `2026-01-${String((index % 28) + 1).padStart(2, "0")}T10:00:00.000Z`;

export const currencyFixtures: readonly CurrencyDto[] = [
  {
    id: "currency-sar",
    code: "SAR",
    nameEn: "Saudi Riyal",
    nameAr: "ريال سعودي",
    symbol: "﷼",
    createdAt: createdAt(1),
    updatedAt: createdAt(1),
  },
  {
    id: "currency-egp",
    code: "EGP",
    nameEn: "Egyptian Pound",
    nameAr: "جنيه مصري",
    symbol: "ج.م",
    createdAt: createdAt(1),
    updatedAt: createdAt(1),
  },
  {
    id: "currency-usd",
    code: "USD",
    nameEn: "US Dollar",
    nameAr: "دولار أمريكي",
    symbol: "$",
    createdAt: createdAt(1),
    updatedAt: createdAt(1),
  },
  {
    id: "currency-aed",
    code: "AED",
    nameEn: "UAE Dirham",
    nameAr: "درهم إماراتي",
    symbol: "د.إ",
    createdAt: createdAt(1),
    updatedAt: createdAt(1),
  },
  {
    id: "currency-eur",
    code: "EUR",
    nameEn: "Euro",
    nameAr: "يورو",
    symbol: "€",
    createdAt: createdAt(1),
    updatedAt: createdAt(1),
  },
];

export const countryFixtures: readonly CountryDto[] = [
  { id: "country-sa", code: "SA", nameEn: "Saudi Arabia", nameAr: "المملكة العربية السعودية" },
  { id: "country-eg", code: "EG", nameEn: "Egypt", nameAr: "مصر" },
  { id: "country-il", code: "IL", nameEn: "Israel", nameAr: "إسرائيل" },
];

export const governorateFixtures: readonly GovernorateDto[] = [
  { id: "governorate-riyadh", nameEn: "Riyadh", nameAr: "الرياض", countryId: "country-sa" },
  { id: "governorate-jeddah", nameEn: "Jeddah", nameAr: "جدة", countryId: "country-sa" },
  { id: "governorate-cairo", nameEn: "Cairo", nameAr: "القاهرة", countryId: "country-eg" },
];

const riyadhRef = { id: "governorate-riyadh", nameEn: "Riyadh", nameAr: "الرياض" };
const saudiRef = {
  id: "country-sa",
  nameEn: "Saudi Arabia",
  nameAr: "المملكة العربية السعودية",
};

export const branchFixtures: readonly BranchDto[] = [
  {
    id: "branch-01",
    nameEn: "Downtown Branch",
    nameAr: "فرع وسط البلد",
    isMain: true,
    isActive: true,
    countryId: null,
    governorateId: null,
    country: null,
    governorate: null,
    latitude: null,
    longitude: null,
    createdAt: createdAt(1),
    updatedAt: createdAt(1),
    accountId: "company-01",
    createdById: "user-1",
    updatedById: null,
  },
  {
    id: "branch-02",
    nameEn: "Riyadh Branch",
    nameAr: "فرع الرياض",
    isMain: false,
    isActive: true,
    countryId: saudiRef.id,
    governorateId: riyadhRef.id,
    country: saudiRef,
    governorate: riyadhRef,
    latitude: 24.7136,
    longitude: 46.6753,
    createdAt: createdAt(2),
    updatedAt: createdAt(2),
    accountId: "company-01",
    createdById: "user-1",
    updatedById: null,
  },
  {
    id: "branch-03",
    nameEn: "Inactive Branch",
    nameAr: "فرع غير نشط",
    isMain: false,
    isActive: false,
    countryId: saudiRef.id,
    governorateId: riyadhRef.id,
    country: saudiRef,
    governorate: riyadhRef,
    latitude: 24.8,
    longitude: 46.7,
    createdAt: createdAt(3),
    updatedAt: createdAt(3),
    accountId: "company-01",
    createdById: "user-1",
    updatedById: null,
  },
];

/**
 * Soft-deleted branches. `POST /branches` matches a create attempt against
 * these by English/Arabic name (within the same account) and answers with an
 * archived-match instead of creating; `PATCH /branches/:id/restore` moves one
 * back into the active list.
 */
export const archivedBranchFixtures: readonly ArchivedBranchListItemDto[] = [
  {
    id: "branch-archived-01",
    nameEn: "Archived Branch",
    nameAr: "فرع مؤرشف",
    isActive: true,
    account: { id: "company-01", nameEn: "Company 01", nameAr: "مؤسسة 01" },
    archivedAt: "2026-01-20T09:00:00.000Z",
  },
];

export const organizationCompanyDtos: readonly CompanyDto[] = Array.from(
  { length: 36 },
  (_, offset) => {
    const index = offset + 1;
    const isActive = index % 3 !== 0;
    const hasAdmin = index % 5 !== 0;
    const currency = currencyFixtures[index % currencyFixtures.length];
    if (currency === undefined) throw new Error("currency fixture missing");

    return {
      id: `company-${String(index).padStart(2, "0")}`,
      type: index % 6 === 0 ? "INDIVIDUAL" : "COMPANY",
      nameEn: `Company ${String(index).padStart(2, "0")}`,
      nameAr: `مؤسسة ${String(index).padStart(2, "0")}`,
      address: `District ${String(index)}`,
      remarks: index % 2 === 0 ? `Seeded company ${String(index)}` : null,
      email: `company${String(index)}@example.test`,
      phoneNumber1: `+966500000${String(index).padStart(2, "0")}`,
      phoneNumber2:
        index % 2 === 0 ? `+966511000${String(index).padStart(2, "0")}` : null,
      fax:
        index % 3 === 0 ? `+966112000${String(index).padStart(2, "0")}` : null,
      createdAt: createdAt(index),
      updatedAt: createdAt(index + 1),
      currencyId: currency.id,
      currency,
      isActive,
      isFullySubscribed: index % 7 === 0,
      attachments:
        index % 3 === 0
          ? [
              {
                id: `attachment-${String(index)}`,
                fileUrl: `https://files.example.test/company-${String(index)}.pdf`,
                extension: "pdf",
                createdAt: createdAt(index),
                updatedAt: createdAt(index),
                createdById: "user-seed",
                updatedById: null,
              },
            ]
          : [],
      admin: hasAdmin
        ? {
            id: `admin-${String(index).padStart(2, "0")}`,
            nameEn: `Admin ${String(index).padStart(2, "0")}`,
            nameAr: `مسؤول ${String(index).padStart(2, "0")}`,
            email: `admin${String(index)}@example.test`,
            username: `admin${String(index)}`,
            isAccountAdmin: true,
            createdAt: createdAt(index),
            updatedAt: createdAt(index + 1),
          }
        : null,
    };
  },
);

const archivedCurrency = currencyFixtures[0];
if (archivedCurrency === undefined) throw new Error("currency fixture missing");

/**
 * Soft-deleted companies. `POST /accounts` matches a create attempt against
 * these by email and answers with an archived-match instead of creating;
 * `PATCH /accounts/:id/restore` moves one back into the active list. Shape
 * mirrors the `ARCHIVED_ACCOUNT_FOUND` response in docs/api/nasam-api.md.
 */
export const organizationArchivedCompanyDtos: readonly (CompanyDto & {
  archivedAt: string;
})[] = [
  {
    id: "eca3b437-add9-4faa-a944-84b1ead3206d",
    type: "COMPANY",
    nameEn: "Archived Company",
    nameAr: "مؤسسة مؤرشفة",
    address: null,
    remarks: null,
    email: "archived-company@example.test",
    phoneNumber1: "+966543335500",
    phoneNumber2: null,
    fax: null,
    createdAt: createdAt(2),
    updatedAt: createdAt(3),
    currencyId: archivedCurrency.id,
    currency: archivedCurrency,
    isActive: false,
    isFullySubscribed: false,
    attachments: [],
    admin: null,
    archivedAt: "2026-01-15T09:00:00.000Z",
  },
  {
    id: "b7c8d9e0-1a2b-4c3d-8e4f-5a6b7c8d9e0f",
    type: "COMPANY",
    nameEn: "Archived Company Two",
    nameAr: "مؤسسة مؤرشفة ثانية",
    address: null,
    remarks: null,
    email: "archived-company-2@example.test",
    phoneNumber1: "+966543335501",
    phoneNumber2: null,
    fax: null,
    createdAt: createdAt(2),
    updatedAt: createdAt(3),
    currencyId: archivedCurrency.id,
    currency: archivedCurrency,
    isActive: false,
    isFullySubscribed: false,
    attachments: [],
    admin: {
      id: "c1d2e3f4-5a6b-4c7d-8e9f-0a1b2c3d4e5f",
      nameEn: "Archived Company Admin",
      nameAr: "مسؤول المؤسسة المؤرشفة",
      email: "archived-company-admin@example.test",
      username: "archived.company.admin",
      isAccountAdmin: true,
      createdAt: createdAt(2),
      updatedAt: createdAt(3),
    },
    archivedAt: "2026-01-08T09:00:00.000Z",
  },
];

/**
 * Soft-deleted admins. `POST /users/account-admin` matches a create attempt
 * against these by email/username and answers with an archived-match instead of
 * creating; `PATCH /users/accounts-admins/:id/restore` moves one back (409 when
 * the account meanwhile gained a different admin). Seeded on `company-10`,
 * which has no active admin, so the happy path restores cleanly.
 */
export const organizationArchivedAdminDtos: readonly (AccountAdminDto & {
  archivedAt: string;
})[] = [
  {
    id: "9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d",
    username: "archived.admin",
    email: "archived-admin@example.test",
    nameEn: "Archived Admin",
    nameAr: "مسؤول مؤرشف",
    phoneNumber: "+966550000099",
    enable2FA: true,
    status: "ACTIVE",
    createdAt: createdAt(2),
    updatedAt: createdAt(3),
    accountId: "company-10",
    // Demoted to the staff counterpart on delete; restore promotes it back.
    typeId: 4,
    type: { id: 4, slug: "CLIENT_EMPLOYEE" },
    account: { id: "company-10", nameEn: "Company 10", nameAr: "مؤسسة 10" },
    archivedAt: "2026-01-15T09:00:00.000Z",
  },
  {
    id: "1a2b3c4d-5e6f-4a7b-8c9d-0e1f2a3b4c5d",
    username: "archived.admin.two",
    email: "archived-admin-2@example.test",
    nameEn: "Archived Admin Two",
    nameAr: "مسؤول مؤرشف ثانٍ",
    phoneNumber: "+966550000098",
    enable2FA: true,
    status: "ACTIVE",
    createdAt: createdAt(2),
    updatedAt: createdAt(3),
    accountId: "company-15",
    typeId: 4,
    type: { id: 4, slug: "CLIENT_EMPLOYEE" },
    account: { id: "company-15", nameEn: "Company 15", nameAr: "مؤسسة 15" },
    archivedAt: "2026-01-08T09:00:00.000Z",
  },
];

export const organizationAccountAdminDtos: readonly AccountAdminDto[] =
  organizationCompanyDtos
    .filter(
      (
        company,
      ): company is CompanyDto & { admin: NonNullable<CompanyDto["admin"]> } =>
        (company.admin ?? null) !== null,
    )
    .map((company, index) => {
      const admin = company.admin;
      const status =
        company.isActive === false
          ? "INACTIVE"
          : index % 4 === 0
            ? "PENDING_ACTIVATION"
            : "ACTIVE";

      return {
        id: admin.id,
        username: admin.username,
        email: admin.email,
        nameEn: admin.nameEn,
        nameAr: admin.nameAr,
        phoneNumber: `+9665500000${String(index + 1).padStart(2, "0")}`,
        enable2FA: index % 2 === 0,
        status,
        createdAt: company.createdAt,
        updatedAt: company.updatedAt,
        accountId: company.id,
        typeId: 3,
        type: { id: 3, slug: "CLIENT_ADMIN" },
        account: {
          id: company.id,
          nameEn: company.nameEn,
          nameAr: company.nameAr,
        },
      };
    });

const departmentNames: readonly (readonly [string, string])[] = [
  ["Operations", "إدارة العمليات"],
  ["Maintenance", "إدارة الصيانة"],
  ["Human Resources", "إدارة الموارد البشرية"],
  ["Finance and Accounting", "إدارة المالية والحسابات"],
  ["Procurement", "إدارة المشتريات"],
  ["Information Technology", "إدارة تقنية المعلومات"],
  ["Logistics", "إدارة الخدمات اللوجستية"],
  ["Quality Assurance", "إدارة ضمان الجودة"],
  ["Safety and Security", "إدارة الأمن والسلامة"],
  ["Customer Service", "إدارة خدمة العملاء"],
  ["Sales", "إدارة المبيعات"],
  ["Marketing", "إدارة التسويق"],
  ["Legal Affairs", "إدارة الشؤون القانونية"],
  ["Fleet Management", "إدارة الأسطول"],
  ["Warehousing", "إدارة المستودعات"],
  ["Planning", "إدارة التخطيط"],
  ["Training", "إدارة التدريب"],
  ["Internal Audit", "إدارة المراجعة الداخلية"],
  ["Public Relations", "إدارة العلاقات العامة"],
  ["Projects", "إدارة المشاريع"],
  ["Administrative Affairs", "إدارة الشؤون الإدارية"],
  ["Contracts", "إدارة العقود"],
  ["Strategy", "إدارة الاستراتيجية"],
];

/** Seeds the in-memory departments repository (no backend endpoint yet) --
 *  more than one page at the default size of 10. */
export const departmentFixtures: readonly Department[] = departmentNames.map(
  ([nameEn, nameAr], index) => ({
    id: String(1001 + index),
    nameEn,
    nameAr,
  }),
);

const departmentAccount = {
  id: "company-01",
  nameEn: "Acme Corp",
  nameAr: "شركة أكمي",
};

/** What the MSW `/departments` handlers serve -- the same names as the mock
 *  store, in the real endpoint's shape. The last one still has users
 *  assigned, so DELETE refuses it with 409. */
export const departmentDtoFixtures: readonly DepartmentDto[] =
  departmentNames.map(([nameEn, nameAr], index) => ({
    id: `department-${String(index + 1).padStart(2, "0")}`,
    nameEn,
    nameAr,
    remarks: index % 3 === 0 ? null : `${nameEn} remarks.`,
    isActive: index % 4 !== 0,
    createdAt: createdAt(index),
    updatedAt: createdAt(index),
    accountId: departmentAccount.id,
    createdById: "user-1",
    updatedById: null,
    account: departmentAccount,
  }));

export const departmentWithUsersId = `department-${String(departmentNames.length).padStart(2, "0")}`;

const archivedDepartmentNames: readonly (readonly [string, string])[] = [
  ["Research and Development", "إدارة البحث والتطوير"],
  ["Tendering", "إدارة المناقصات"],
  ["Archives", "إدارة الأرشيف"],
  ["Facilities", "إدارة المرافق"],
  ["Government Relations", "إدارة العلاقات الحكومية"],
  ["Investment", "إدارة الاستثمار"],
  ["Risk Management", "إدارة المخاطر"],
  ["Compliance", "إدارة الالتزام"],
  ["Customs Clearance", "إدارة التخليص الجمركي"],
  ["Real Estate", "إدارة العقارات"],
  ["Translation", "إدارة الترجمة"],
  ["Events", "إدارة الفعاليات"],
];

/** Seeds the mock repository's archive -- more than one page at 10. */
export const archivedDepartmentFixtures: readonly ArchivedDepartment[] =
  archivedDepartmentNames.map(([nameEn, nameAr], index) => ({
    id: String(2001 + index),
    nameEn,
    nameAr,
    archivedAt: `2026-08-${String(index + 1).padStart(2, "0")}T09:00:00.000Z`,
  }));

/** What the MSW `GET /departments/archived` handler serves. */
export const archivedDepartmentDtoFixtures: readonly ArchivedDepartmentDto[] =
  archivedDepartmentNames.map(([nameEn, nameAr], index) => ({
    id: `archived-department-${String(index + 1).padStart(2, "0")}`,
    nameEn,
    nameAr,
    remarks: null,
    isActive: index % 2 === 0,
    createdAt: createdAt(index),
    updatedAt: createdAt(index),
    accountId: departmentAccount.id,
    createdById: "user-1",
    updatedById: null,
    account: departmentAccount,
    archivedAt: `2026-08-${String(index + 1).padStart(2, "0")}T09:00:00.000Z`,
  }));
